<template>
  <loading-logo v-if="isFetching" />
  <main v-else>
    <section style="padding-left: 8px">
      <HeaderSection
        :title="$t('message.pages.drm.drmVideos.title')"
        :subtitle="$t('message.pages.drm.drmVideos.description')"
      />
    </section>
    <section v-if="loadedVideos.length > 0 || loadedFolders.length > 0">
      <div
        class="d-flex align-center justify-space-between my-6"
        style="padding-left: 8px"
      >
        <div style="width: 368px">
          <v-input
            class="search-bar"
            v-model="searchKeysText"
            size="small"
            hide-details
            :placeholder="
              $t('message.pages.videosDataTable.searchBar.placeholder')
            "
            ><template v-slot:prepend-inner>
              <v-icon class="neutral-60--text" size="16"> panda-search </v-icon>
            </template></v-input
          >
        </div>
        <span class="body-regular-smaller textType6--text ml-2"
          >{{ filteredKeys.length }}
          {{
            filteredKeys.length > 1
              ? $t('message.videos').toLowerCase()
              : $t('message.video').toLowerCase()
          }}
        </span>
      </div>
      <BaseTable
        style="padding-left: 4px"
        :items="filteredKeys"
        :bodyHeight="'auto'"
        :maxHeight="450"
        :minWidth="isMobileBreakpoint ? 300 : 400"
        :columns="tableColumns"
        :selectable="true"
        :selected-items="selectedItems"
        :textTitleEmpty="$t('message.pages.drm.drmVideos.notFoundSearchTitle')"
        :textDescriptionEmpty="
          $t('message.pages.drm.drmVideos.notFoundSearchDescription')
        "
        @sort="handleSort"
        @select-all="selectAllTokens"
        @select-row="checkboxSelectionToken"
        @row-click="handleOpenItem"
        :menu-options="[
          {
            label: $t('message.pages.drm.drmVideos.openVideo'),
            action: 'open',
            condition: (row) => row.title,
          },
          {
            label: $t('message.pages.drm.drmVideos.openFolder'),
            action: 'open',
            condition: (row) => row.name,
          },
          {
            label: $t('message.pages.drm.drmVideos.removeVideo'),
            action: 'delete',
            textColor: { color: 'red' },
            condition: (row) => row.title,
          },
          {
            label: $t('message.pages.drm.drmVideos.removeFolder'),
            action: 'delete',
            textColor: { color: 'red' },
            condition: (row) => row.name,
          },
        ]"
        @menu-action="handleMenuAction"
      >
        <template #titleCommon="{ row }">
          <v-row
            no-gutters
            style="position: relative; max-width: 100%"
            align="center"
            @mousemove="handlePreviewMousemove($event, row)"
            @mouseleave="($event) => handlePreviewMouseleave($event, row)"
          >
            <template>
              <span
                class="text-truncate mouse-pointer"
                :title="row.title || row.name"
              >
                <v-icon
                  size="16"
                  :color="$vuetify.theme.dark ? '#203CF8' : '#7282f3'"
                >
                  {{ row.title ? 'panda-video' : 'mdi-folder' }}
                </v-icon>
                {{ row.title || row.name }}
              </span>
            </template>
          </v-row>
        </template>
      </BaseTable>
      <v-row class="mt-2" no-gutters style="padding-left: 8px">
        <base-btn type="secondary" size="small" @click="openModal">{{
          $t('message.pages.drm.addVideo')
        }}</base-btn></v-row
      >
      <v-menu
        v-model="showPreview"
        max-width="180px"
        :position-x="x"
        :position-y="y"
        absolute
        offset-y
        :transition="null"
        content-class="elevation-0"
        style="z-index: 9999"
      >
        <v-row
          class="d-flex flex-column"
          no-gutters
          justify="center"
          align="center"
        >
          <img
            :src="selectedPreview"
            onload="this.style='display: block'"
            onerror="this.style='display: none'"
            max-width="180"
          />
        </v-row>
      </v-menu>
    </section>
    <section
      v-else
      class="align-center justify-center"
      style="height: 100%; display: grid"
    >
      <v-row
        class="d-flex flex-column"
        no-gutters
        justify="center"
        align="center"
      >
        <img
          class="emptyStateTitle mb-4"
          src="@/assets/videosEmptyState.svg"
          max-width="240"
        />
        <span class="body-regular-large textType3--text">
          {{ $t('message.pages.drm.drmVideos.titleEmpty') }}</span
        >
        <span
          class="body-regular-small textType6--text"
          style="text-align: center; white-space: pre-line"
        >
          {{ $t('message.pages.drm.drmVideos.descriptionEmpty') }}
        </span>
        <base-btn
          class="mt-5"
          size="small"
          type="secondary"
          v-if="!isMobileBreakpoint"
          @click="openModal"
        >
          <span> {{ $t('message.pages.drm.addVideo') }} </span>
        </base-btn>
      </v-row>
    </section>
    <base-float-menu v-if="selectedItems.length > 0">
      <div style="display: grid">
        <span class="body-bold-smaller neutral-20-dark--text">
          {{ selectedItems.length }}
          {{
            selectedItems.length > 1
              ? $t('message.selectedItems')
              : $t('message.selectedItem')
          }}</span
        >
        <span class="selectedItensSize">{{ selectedItemsSize }}</span>
      </div>
      <div style="border-left: 1px solid #d1d1d1">
        <base-btn
          @click="handleDeleteItems()"
          class="ml-4"
          size="small"
          type="danger"
        >
          {{ $t('message.remove') }}
        </base-btn>
      </div>
    </base-float-menu>
    <drm-add-video
      :openSelectPanda="openSelectPanda"
      :selectedGroup="selectedDrmGroup"
      :allDrmGroups="allDrmGroups"
      @update:openSelectPanda="openSelectPanda = $event"
    />
  </main>
</template>
<script>
import { mapState, mapMutations } from 'vuex'
import drmApi from '@/services/drmApi'
import BaseTable from '../../../components/BaseTable.vue'
import HeaderSection from './HeaderSection.vue'
import BaseFloatMenu from '@/components/BaseFloatMenu.vue'
import DrmAddVideo from './DrmAddVideo.vue'
import foldersApi from '@/services/foldersApi'
import videosApi from '@/services/videosApi'
import LoadingLogo from '../../../components/LoadingLogo.vue'
export default {
  name: 'DrmListKeys',
  components: {
    BaseTable,
    HeaderSection,
    BaseFloatMenu,
    DrmAddVideo,
    LoadingLogo,
  },
  props: {
    selectedDrmGroup: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isFetching: false,
      loadedVideos: [],
      loadedFolders: [],
      windowWidth: window.innerWidth,
      searchKeysText: '',
      tokenNameOrder: 'asc',
      showPreview: false,
      openSelectPanda: false,
      videosLimit: 2000,
      allDrmGroups: [],
      x: 0,
      y: 0,
      previewTimeout: false,
      clearPreviewTimeout: false,
      selectedPreview: '',
      selectedItems: [],
      selectedGroup: {
        id: null,
      },
    }
  },
  computed: {
    ...mapState({
      mainFolders: (state) => state.user.mainFolders,
    }),
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    tableColumns() {
      const w = this.windowWidth

      const getWidth = (
        large,
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7
      ) => {
        if (w < 1360) return step7
        if (w < 1460) return step6
        if (w < 1560) return step5
        if (w < 1660) return step4
        if (w < 1760) return step3
        if (w < 1860) return step2
        if (w < 1960) return step1
        return large
      }

      return [
        {
          prop: 'title',
          label: this.$t('message.name'),
          width: this.isMobileBreakpoint
            ? 280
            : getWidth(800, 760, 640, 580, 570, 560, 450, 440),
        },
      ]
    },
    window() {
      return window
    },

    filteredKeys() {
      const allItems = [...this.loadedFolders, ...this.loadedVideos]
      return allItems.filter(
        (item) =>
          item.title
            ?.toLowerCase()
            ?.includes(this.searchKeysText.toLowerCase()) ||
          item.name?.toLowerCase()?.includes(this.searchKeysText.toLowerCase())
      )
    },
    checkboxIndeterminateStatusTokens() {
      return (
        this.selectedItems.length > 0 &&
        this.selectedItems.length < this.loadedVideos.length
      )
    },
    anyTokenSelected() {
      return this.selectedItems.length > 0
    },
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_FIRST_ACCESS',
      'UPDATE_ROUTED_VIDEO',
      'UPDATE_MAIN_VIDEOS',
      'UPDATE_MAIN_FOLDERS',
      'UPDATE_ALL_FOLDERS',
      'UPDATE_SUB_FOLDER_VIDEOS',
      'UPDATE_SUB_FOLDER',
      'UPDATE_DID_FETCH_VIDEOS',
      'UPDATE_SHOULD_FETCH_ALL_VIDEOS',
      'UPDATE_DELETED_FOLDERS',
      'UPDATE_USER_PANDA_BRANDING_STATE',
    ]),
    updateWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    handleMenuAction({ action, row }) {
      switch (action) {
        case 'open':
          this.handleOpenItem(row)
          break
        case 'delete':
          this.handleDeleteItems(row)
          break
        case 'view':
          console.log(row)
          break
      }
    },
    async handleDeleteItems(row) {
      this.isFetching = true
      let foldersIds = []
      let videosIds = []
      const selectedItems = row ? [row] : this.selectedItems
      for (let item of selectedItems) {
        if (item.name) foldersIds.push(item.id)
        if (item.title) videosIds.push(item.id)
      }
      try {
        let ret = await drmApi.drmUpdateVideos(this.selectedDrmGroup.id, {
          videos: {
            action: 'delete',
            video_ids: videosIds,
          },
          folders: {
            action: 'delete',
            folder_ids: foldersIds,
          },
        })
        if (!window.db) {
          this.$root.$emit('initDexie')
        }
        this.selectedItems = []
        this.selectedDrmGroup = { ...this.selectedDrmGroup, ...ret.data }
        await this.getGroupVideos(this.selectedDrmGroup)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          type: 'success',
          text: this.$t(
            'message.pages.drm.drmVideos.notFoundSearchDescription'
          ),
        })
        this.isFetching = false
        this.isFetching = false
        console.log('ret', ret)
      } catch (e) {
        this.isFetching = false
        console.log('e', e)
      }
      this.isFetching = false
    },
    async getDrmGroups() {
      try {
        let ret = await drmApi.getDrmGroups()
        this.allDrmGroups = ret.data
      } catch (error) {
        console.log(error)
      }
    },
    handleSort({ prop, order }) {
      this.filteredKeys.sort((a, b) => {
        const valA = a[prop]
        const valB = b[prop]
        if (valA == null || valB == null) return 0

        if (order === 'asc') {
          return valA > valB ? 1 : valA < valB ? -1 : 0
        } else {
          return valA < valB ? 1 : valA > valB ? -1 : 0
        }
      })
    },
    handleOpenItem(row) {
      if (row.title)
        this.$router.push({ name: 'VideoPage', params: { id: row.id } })
      if (row.name)
        this.$router.push({ name: 'Folders', params: { id: row.id } })
    },
    openImportKeysModal() {
      this.$root.$emit('openDrmImportModal')
    },
    selectAllTokens() {
      if (
        this.selectedItems.length ===
        [...this.loadedVideos, ...this.loadedFolders].length
      ) {
        this.selectedItems = []
        return
      }
      this.selectedItems = [...this.loadedVideos, ...this.loadedFolders]
    },
    handlePreviewMouseleave() {
      clearTimeout(this.clearPreviewTimeout)
      this.clearPreviewTimeout = setTimeout(() => {
        this.showPreview = false
        this.selectedPreview = false
      }, 600)
    },
    handlePreviewMousemove(e, row) {
      if (
        this.selectedPreview &&
        row.hasOwnProperty('preview') &&
        this.selectedPreview == row.preview
      ) {
        this.x = e.clientX - 24
        this.y = e.clientY + 18
      }

      if (
        !this.selectedPreview &&
        row.hasOwnProperty('preview') &&
        !this.showPreview
      ) {
        clearTimeout(this.clearPreviewTimeout)
        clearTimeout(this.previewTimeout)
        this.previewTimeout = setTimeout(() => {
          this.y = e.clientY + 18
          this.x = e.clientX - 24
          this.selectedPreview = row.preview

          this.showPreview = false
          this.$nextTick(() => {
            this.showPreview = true
          })
        }, 200)
      } else if (this.selectedPreview && this.selectedPreview != row.preview) {
        this.showPreview = false
        this.selectedPreview = false
      }
    },
    openModal() {
      this.openSelectPanda = true
    },
    switchTokenNameOrder() {
      if (!this.tokenNameOrder || this.tokenNameOrder === 'desc')
        return (this.tokenNameOrder = 'asc')
      if (this.tokenNameOrder === 'asc') return (this.tokenNameOrder = 'desc')
    },
    checkIfSelectedToken(item) {
      let foundItem = this.selectedItems.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    checkboxSelectionToken(item) {
      let alreadySelected = this.checkIfSelectedToken(item)
      if (alreadySelected) {
        let newArr = [
          ...this.selectedItems.filter((lookToken) => {
            return lookToken.id !== item.id
          }),
        ]
        this.selectedItems = [...newArr]
      } else {
        this.selectedItems.push(item)
      }
    },
    async videosRequest(page) {
      let queryObject = {
        limit: this.videosLimit,
      }
      if (page) queryObject['page'] = page
      try {
        const ret = await videosApi.getAllVideosAndBin(queryObject)
        console.log(ret)
        return ret
      } catch (error) {
        this.isFetching = false
        this.formatErr(error)
      }
    },
    async getGroupVideos(group) {
      this.loadedVideos = []
      this.loadedFolders = []
      let toBeFetchVideos = group.video_ids

      let toBeFetchFolders = group.folder_ids
      let newLoadedArray = []
      let newFolderArray = []
      for (let videoId of toBeFetchVideos) {
        let foundVideo = await window.db.videos.get(videoId)
        if (foundVideo?.title) {
          newLoadedArray.push(foundVideo)
        }
      }
      for (let folderId of toBeFetchFolders) {
        let foundFolder = this.mainFolders.find((el) => el.id === folderId)
        if (foundFolder?.name) {
          newFolderArray.push(foundFolder)
        }
      }
      this.loadedFolders = newFolderArray
      this.loadedVideos = newLoadedArray
      console.log('loadedVideos', this.loadedFolders)
    },
    async getAllAccountVideos() {
      try {
        const videos = await this.videosRequest()
        let filteredFoldersIds = []
        let videosResponse = videos
        for (let folderId in videosResponse) {
          filteredFoldersIds.push(folderId)
        }
        await window.db.videos.clear()
        for (let folderId of filteredFoldersIds) {
          let arrToBeAdded = videos[folderId].map((obj) => ({
            ...obj,
            folder_id: folderId,
          }))
          await window.db.videos.bulkPut(arrToBeAdded)
        }
      } catch (error) {
        this.isFetching = false
      }
    },
    async readUserFolders() {
      try {
        let ret = await foldersApi.getUserFolders()
        let notDeletedFolders = ret.data.folders.filter(
          (folder) => folder.status != false
        )
        let deletedFolders = ret.data.folders.filter(
          (folder) => folder.status === false
        )
        this.UPDATE_MAIN_FOLDERS(notDeletedFolders)
        this.UPDATE_DELETED_FOLDERS(deletedFolders)
        this.UPDATE_ALL_FOLDERS(ret.data.folders)
      } catch (e) {
        console.error(e)
      }
    },
  },
  created() {
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
  },
  async mounted() {
    this.isFetching = true
    window.addEventListener('resize', this.updateWindowWidth)
    await this.getGroupVideos(this.selectedDrmGroup)
    this.$root.$on('reloadVideos', async (selectedDrmGroup) => {
      this.openSelectPanda = false
      this.isFetching = true
      this.selectedDrmGroup = { ...this.selectedDrmGroup, ...selectedDrmGroup }
      if (!window.db) {
        this.$root.$emit('initDexie')
      }
      // await this.getAllAccountVideos()
      // await this.readUserFolders()
      await this.getGroupVideos(this.selectedDrmGroup)
      this.isFetching = false
    })
    await this.getDrmGroups()
    this.isFetching = false
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowWidth)
  },
}
</script>
<style lang="scss">
.displayBlock {
  display: block;
}

.displayNone {
  display: none;
}

.t-header {
  display: none;
}

.mouse-pointer {
  cursor: pointer;
}
</style>
